/**
 * Inbox Zero: l'AI categorizza le non lette e propone un'azione per ognuna.
 * L'utente rivede, aggiusta e applica in blocco (archivia / elimina / segna letto).
 * NB: le azioni agiscono nella vista Blagomail (sync IMAP lato server: follow-up).
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Loader2, Archive, Trash2, MailOpen, Check, Inbox, History } from 'lucide-react';
import { toast } from 'sonner';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { Button } from '@/components/ui/button';
import { useInbox } from '@/stores/inbox.store';
import {
  inboxZeroApi, accountsApi,
  type InboxZeroCategory, type InboxZeroAction, type InboxZeroCategorized,
} from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';

type Choice = InboxZeroAction | 'skip';

const CAT: Record<InboxZeroCategory, { label: string; cls: string; def: Choice }> = {
  to_reply:   { label: 'Da rispondere', cls: 'text-amber-600 bg-amber-500/10',   def: 'skip' },
  to_archive: { label: 'Da archiviare', cls: 'text-blue-600 bg-blue-500/10',     def: 'archive' },
  newsletter: { label: 'Newsletter',    cls: 'text-purple-600 bg-purple-500/10', def: 'archive' },
  promo:      { label: 'Promozioni',    cls: 'text-pink-600 bg-pink-500/10',     def: 'archive' },
  to_delete:  { label: 'Da eliminare',  cls: 'text-red-600 bg-red-500/10',       def: 'delete' },
};
const CAT_ORDER: InboxZeroCategory[] = ['to_reply', 'to_archive', 'newsletter', 'promo', 'to_delete'];
const ACTION_LABEL: Record<Choice, string> = {
  skip: 'Lascia', archive: 'Archivia', delete: 'Elimina', mark_read: 'Segna letto',
};

export function InboxZeroPage() {
  const qc = useQueryClient();
  const selectedAccountId = useInbox((s) => s.selectedAccountId);
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => accountsApi.list() });
  const accountId =
    selectedAccountId ??
    accountsQuery.data?.accounts.find((a) => a.is_default)?.id ??
    accountsQuery.data?.accounts[0]?.id ??
    null;

  const [items, setItems] = useState<InboxZeroCategorized[]>([]);
  const [choices, setChoices] = useState<Record<string, Choice>>({});

  const historyQuery = useQuery({ queryKey: ['inbox-zero-history'], queryFn: () => inboxZeroApi.history() });

  const previewMut = useMutation({
    mutationFn: () => inboxZeroApi.preview(accountId!, 30),
    onSuccess: (res) => {
      setItems(res.categorized);
      const init: Record<string, Choice> = {};
      for (const c of res.categorized) init[c.message_id] = CAT[c.category]?.def ?? 'skip';
      setChoices(init);
      if (res.categorized.length === 0) toast.success('Nessuna mail da elaborare — sei già a Inbox Zero! 🎉');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Analisi fallita'),
  });

  const applyMut = useMutation({
    mutationFn: () => {
      const actions = items
        .map((it) => ({ message_id: it.message_id, action: choices[it.message_id] }))
        .filter((a): a is { message_id: string; action: InboxZeroAction } => a.action !== 'skip' && a.action !== undefined);
      return inboxZeroApi.apply(accountId!, actions);
    },
    onSuccess: (res) => {
      toast.success(`Fatto: ${res.archived} archiviate, ${res.deleted} eliminate, ${res.marked} segnate lette`);
      setItems([]);
      setChoices({});
      qc.invalidateQueries({ queryKey: ['messages'] });
      qc.invalidateQueries({ queryKey: ['inbox-zero-history'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Applicazione fallita'),
  });

  const grouped = useMemo(() => {
    const g: Record<InboxZeroCategory, InboxZeroCategorized[]> = {
      to_reply: [], to_archive: [], newsletter: [], promo: [], to_delete: [],
    };
    for (const it of items) (g[it.category] ?? g.to_archive).push(it);
    return g;
  }, [items]);

  const plannedCount = items.filter((it) => choices[it.message_id] && choices[it.message_id] !== 'skip').length;

  function setCategoryChoice(cat: InboxZeroCategory, choice: Choice) {
    setChoices((prev) => {
      const next = { ...prev };
      for (const it of grouped[cat]) next[it.message_id] = choice;
      return next;
    });
  }

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-8 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Inbox Zero</h1>
              <p className="text-sm text-muted-foreground">L'AI categorizza le non lette e propone un'azione per ognuna.</p>
            </div>
            <Button onClick={() => previewMut.mutate()} disabled={!accountId || previewMut.isPending}>
              {previewMut.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              Analizza non lette
            </Button>
          </div>

          {!accountId && (
            <p className="text-sm text-muted-foreground">Nessun account selezionato.</p>
          )}

          {/* Risultati */}
          {items.length > 0 && (
            <>
              <div className="sticky top-0 z-10 -mx-2 px-2 py-2 bg-background/90 backdrop-blur flex items-center justify-between border-b">
                <span className="text-sm text-muted-foreground">
                  {items.length} non lette · <strong className="text-foreground">{plannedCount}</strong> azioni pianificate
                </span>
                <Button size="sm" onClick={() => applyMut.mutate()} disabled={plannedCount === 0 || applyMut.isPending}>
                  {applyMut.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                  <Check className="h-4 w-4 mr-1.5" /> Applica {plannedCount > 0 ? `(${plannedCount})` : ''}
                </Button>
              </div>

              {CAT_ORDER.filter((c) => grouped[c].length > 0).map((cat) => (
                <div key={cat} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className={cn('text-xs font-semibold uppercase tracking-wide rounded-full px-2.5 py-1', CAT[cat].cls)}>
                      {CAT[cat].label} · {grouped[cat].length}
                    </span>
                    <div className="flex items-center gap-1">
                      {(['archive', 'delete', 'mark_read', 'skip'] as Choice[]).map((ch) => (
                        <button
                          key={ch}
                          type="button"
                          onClick={() => setCategoryChoice(cat, ch)}
                          className="text-[11px] text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded hover:bg-muted"
                        >
                          tutti: {ACTION_LABEL[ch]}
                        </button>
                      ))}
                    </div>
                  </div>
                  {grouped[cat].map((it) => (
                    <div key={it.message_id} className="rounded-lg border p-3 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{it.subject || '(senza oggetto)'}</div>
                        <div className="text-xs text-muted-foreground truncate">{it.from}</div>
                        {it.reason && <div className="text-xs text-muted-foreground/80 mt-1 italic">“{it.reason}”</div>}
                      </div>
                      <select
                        value={choices[it.message_id] ?? 'skip'}
                        onChange={(e) => setChoices((p) => ({ ...p, [it.message_id]: e.target.value as Choice }))}
                        className="text-sm rounded-md border bg-background px-2 py-1 outline-none focus:ring-1 focus:ring-primary"
                      >
                        <option value="skip">Lascia</option>
                        <option value="archive">Archivia</option>
                        <option value="delete">Elimina</option>
                        <option value="mark_read">Segna letto</option>
                      </select>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}

          {items.length === 0 && !previewMut.isPending && (
            <div className="text-center py-12 text-muted-foreground">
              <Inbox className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <div className="text-sm">Premi “Analizza non lette” per iniziare.</div>
            </div>
          )}

          {/* Cronologia */}
          {(historyQuery.data?.runs.length ?? 0) > 0 && (
            <div className="pt-4 border-t space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <History className="h-3.5 w-3.5" /> Cronologia
              </div>
              {historyQuery.data!.runs.slice(0, 8).map((r) => (
                <div key={r.id} className="text-xs text-muted-foreground flex items-center gap-3">
                  <span>{r.completed_at ? new Date(r.completed_at).toLocaleString('it-IT') : '—'}</span>
                  <span className="inline-flex items-center gap-1"><Archive className="h-3 w-3" /> {r.messages_archived}</span>
                  <span className="inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> {r.messages_deleted}</span>
                  <span className="inline-flex items-center gap-1"><MailOpen className="h-3 w-3" /> {r.messages_total}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
